import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import classes from "./ServiceErstellenPage.module.css";
import { useServiceErstellen } from '../../store/serviceErstellenStore';
import { useSelector } from 'react-redux';
import { useCreateCreative } from "../../hooks/creative/useCreateCreative";
import { useCreateStudioCreative } from "../../hooks/creative/useCreateStudioCreative";
import { getUrl } from 'aws-amplify/storage';
import { v4 as uuid } from 'uuid';
import { NavBarContext } from "../../App";
import awsConfig from '../../config/aws-config';
import CancelPopUp from "../../components/StudioErstellen/CancelPopUp.jsx";
import LoadingCreationModal from "../../components/StudioErstellen/LoadingCreationModal.jsx";
import ErrorMessage from "../../components/StudioErstellen/ErrorMessage.jsx";
import { deleteOwnedMedia, uploadOwnedMedia } from "../../util/ownedMedia";
import { getVisualTypesFromCreativeCategories } from "../../util/creativeTypeUtils";
import {
    trackServiceCreationComplete,
    trackServiceCreationStep
} from "../../util/tracking";

// Step components - to be created
import ChooseStudio from "./Steps/ChooseStudio.jsx";
import ChooseCreativeProfile from "./Steps/ChooseCreativeProfile.jsx";
import AddUserInfos from "./Steps/AddUserInfos.jsx";
import { notifySaveError } from '../../util/notifyActionError';
import AboutService from "./Steps/AboutService.jsx";

const stepRoutes = [
    'studio-wahlen',
    'kreativprofil',
    'ueber-dich',
    'dein-service'
];

const SERVICE_PUBLISH_ERROR_MESSAGE = 'Service konnte nicht veröffentlicht werden. Bitte versuche es erneut.';
const USER_SAFE_SERVICE_ERROR_PREFIXES = [
    'Bitte ',
    'Die ',
    'Das ',
    'Der ',
    'Dein ',
    'Profilbild ',
    'Creative-Profil ',
    'Service ',
    'Studio '
];

const getServicePublishErrorMessage = (error) => {
    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    if (!message) return SERVICE_PUBLISH_ERROR_MESSAGE;

    const isGermanUserMessage =
        /[äöüÄÖÜß]/.test(message) ||
        USER_SAFE_SERVICE_ERROR_PREFIXES.some((prefix) => message.startsWith(prefix));

    return isGermanUserMessage ? message : SERVICE_PUBLISH_ERROR_MESSAGE;
};

export const ServiceErstellenPage = () => {
    const { serviceData, clearServiceData, updateServiceData, markStepCompleted } = useServiceErstellen();
    const user = useSelector((state) => state.auth.user);
    const userID = user?._id;
    const { handleCreateCreative, loading: createCreativeLoading, error: createCreativeError } = useCreateCreative();
    const { handleCreateStudioCreative, loading: linkLoading, error: linkError } = useCreateStudioCreative();
    const { step } = useParams();
    const navigate = useNavigate();
    const { isPhoneSize } = useContext(NavBarContext);

    const [hasError, setHasError] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishError, setPublishError] = useState(null);
    const isPublishingRef = useRef(false);
    const isNavigatingRef = useRef(false);
    const lastTrackedServiceCreationStepRef = useRef(null);

    // Exit confirmation modal state
    const [showExitModal, setShowExitModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);

    const chooseStudioValidation = useRef(() => true);
    const chooseCreativeProfileValidation = useRef(() => true);
    const addUserInfosValidation = useRef(() => true);
    const aboutServiceValidation = useRef(() => true);

    const getCurrentSlideFromURL = () => {
        if (!step) return 0;
        const index = stepRoutes?.indexOf(step);
        return index >= 0 ? index : 0;
    };

    const [currentSlide, setCurrentSlide] = useState(getCurrentSlideFromURL());

    const slides = [
        <ChooseStudio userID={userID} validate={chooseStudioValidation}/>,
        <ChooseCreativeProfile validate={chooseCreativeProfileValidation}/>,
        <AddUserInfos validate={addUserInfosValidation}/>,
        <AboutService validate={aboutServiceValidation}/>
    ];

    const totalSlides = slides?.length;

    // Data validation functions that check if step data is complete in the store
    const validateStepData = (stepIndex) => {
        switch(stepIndex) {
            case 0: // ChooseStudio
                return serviceData?.selectedStudioId?.trim()?.length > 0;

            case 1: // ChooseCreativeProfile
                // Valid if creativeType is set (should be "VISUAL" or "MUSIC")
                const creativeType = serviceData?.creativeType || "";
                return creativeType === "VISUAL" || creativeType === "MUSIC";

            case 2: // AddUserInfos
                // Check displayName - from serviceData or fallback to user's name
                const displayName = serviceData?.displayName?.trim() || 
                    (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : '') ||
                    user?.displayName?.trim() || '';
                
                // Check profilePicture - accept file (newly selected) or url (existing)
                const hasProfilePicture = 
                    (serviceData?.profilePicture?.file) || 
                    (serviceData?.profilePicture?.url) || 
                    (user?.profilePicture);
                
                return displayName.length > 0 &&
                       serviceData?.jobTitle?.trim()?.length > 0 &&
                       hasProfilePicture;

            case 3: // AboutService
                return serviceData?.description?.trim()?.length >= 10 &&
                       serviceData?.description?.trim()?.length <= 100 &&
                       serviceData?.skills?.length >= 3;

            default:
                return false;
        }
    };

    const canAccessStep = (stepIndex) => {
        if (stepIndex === 0) return true;

        // Check if all previous steps have valid data
        for (let i = 0; i < stepIndex; i++) {
            if (!validateStepData(i)) {
                return false;
            }
        }

        return true;
    };

    const getFirstIncompleteStep = () => {
        for (let i = 0; i < totalSlides; i++) {
            if (!validateStepData(i)) {
                return i;
            }
        }
        return 0;
    };

    const validateCurrentSlide = async () => {
        const validateFunctions = [
            chooseStudioValidation.current,
            chooseCreativeProfileValidation.current,
            addUserInfosValidation.current,
            aboutServiceValidation.current
        ];

        const isValid = await validateFunctions[currentSlide]();
        setHasError(!isValid);
        return isValid;
    };

    // Sync currentSlide with URL changes (for browser back/forward)
    useEffect(() => {
        const newSlide = getCurrentSlideFromURL();
        if (!canAccessStep(newSlide)) {
            // Redirect to the first incomplete step
            const firstIncomplete = getFirstIncompleteStep();
            navigate(`/services-erstellen/${stepRoutes[firstIncomplete]}`, { replace: true });
            return;
        }

        if (newSlide !== currentSlide) {
            setCurrentSlide(newSlide);
        }
    }, [step, currentSlide, navigate]);

    // Navigate to first step if no step is specified
    useEffect(() => {
        if (!step) {
            navigate(`/services-erstellen/${stepRoutes[0]}`, { replace: true });
        }
    }, [step, navigate]);

    const progressPercentage = ((currentSlide + 1) / totalSlides) * 100;

    const carouselRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const carouselEl = carouselRef?.current;
        if (!carouselEl) return;

        if (isPhoneSize && containerRef?.current) {
            const containerEl = containerRef.current;
            const targetTopWithinContainer = carouselEl.offsetTop - (containerEl.offsetTop || 0);
            containerEl.scrollTo({ top: targetTopWithinContainer - 100, left: 0, behavior: 'auto' });
        } else {
            const rect = carouselEl.getBoundingClientRect();
            const absoluteTop = rect.top + (window.pageYOffset || document.documentElement.scrollTop || 0);
            window.scrollTo({ top: absoluteTop, left: 0, behavior: 'auto' });
        }
    }, [currentSlide, isPhoneSize]);

    // Check if user has entered any data worth saving
    const hasUnsavedData = () => {
        return !!(
            serviceData?.selectedStudioId ||
            serviceData?.creativeType ||
            serviceData?.creativeCategories ||
            serviceData?.displayName ||
            serviceData?.jobTitle ||
            serviceData?.description ||
            serviceData?.profilePicture ||
            serviceData?.skills?.length > 0
        );
    };

    // Warn before closing/refreshing browser tab
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedData() && !isPublishingRef.current) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [serviceData]);

    // Handle link click interception
    const handleExitClick = (e, targetPath) => {
        if (hasUnsavedData() && !isNavigatingRef.current) {
            e.preventDefault();
            setPendingNavigation(() => () => navigate(targetPath));
            setShowExitModal(true);
        }
    };

    // Confirm exit and proceed with navigation
    const confirmExit = () => {
        isNavigatingRef.current = true;
        clearServiceData();
        setShowExitModal(false);

        if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
        }
    };

    // Cancel exit and stay in wizard
    const cancelExit = () => {
        setShowExitModal(false);
        setPendingNavigation(null);
    };

    // Handle browser back button with popstate event
    useEffect(() => {
        let lastPath = window.location.pathname;

        const handlePopState = (event) => {
            const currentPath = window.location.pathname;
            const wasInWizard = lastPath.includes('/services-erstellen');
            const isStillInWizard = currentPath.includes('/services-erstellen');

            const isLeavingWizard = wasInWizard && !isStillInWizard;

            if (isLeavingWizard && !isNavigatingRef.current && !isPublishingRef.current) {
                const currentStepRoute = step || stepRoutes[currentSlide] || stepRoutes[0];

                window.history.pushState(
                    { wizardIntercepted: true },
                    '',
                    `/services-erstellen/${currentStepRoute}`
                );

                setPendingNavigation(() => () => {
                    isNavigatingRef.current = true;
                    window.history.back();
                });
                setShowExitModal(true);

                return;
            }

            lastPath = currentPath;
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [currentSlide, step, serviceData, isPublishing]);

    const nextSlide = async () => {
        const isValid = await validateCurrentSlide();
        if (isValid) {
            markStepCompleted(currentSlide);

            if (currentSlide < totalSlides - 1) {
                const nextStep = stepRoutes[currentSlide + 1];
                navigate(`/services-erstellen/${nextStep}`);
            }
        }
    };

    const prevSlide = () => {
        if (currentSlide === 0) {
            setPendingNavigation(() => () => navigate('/services-studios'));
            setShowExitModal(true);
            return;
        }else {
            navigate(-1);
        }
    };

    const stepTexts = [
        "Kreativer Service",
        "Schritt 1: Art des Kreativprofils",
        "Schritt 2: Über dich",
        "Schritt 2: Dein kreativer Service"
    ];
    const currentStepText = stepTexts[currentSlide];

    useEffect(() => {
        const signature = `${currentSlide}:${currentStepText}:${serviceData?.creativeType || ''}`;
        if (lastTrackedServiceCreationStepRef.current === signature) return;

        lastTrackedServiceCreationStepRef.current = signature;
        trackServiceCreationStep({
            stepIndex: currentSlide + 1,
            stepName: currentStepText,
            creativeType: serviceData?.creativeType,
            source: 'service_creation'
        });
    }, [currentSlide, currentStepText, serviceData?.creativeType]);

    const handleExit = () => {
        clearServiceData();
        navigate('/');
    };

    const uploadMedia = async (imageData, creativeId) => {
        try {
            if (!creativeId) {
                throw new Error('Creative-Profil konnte nicht vorbereitet werden.');
            }

            let file = imageData?.file;
            
            // If no file but URL exists, check what type of URL it is
            if (!file && imageData?.url) {
                const url = imageData.url;
                
                // Check if it's a data URL (from newly selected image)
                if (url.startsWith('data:')) {
                    // Convert data URL to file
                    const response = await fetch(url);
                    const blob = await response.blob();
                    const fileName = 'profile.jpg';
                    file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                }
                // Check if it's a CloudFront URL (existing user image)
                else if (url.includes('cloudfront.net')) {
                    // Extract S3 key from CloudFront URL to avoid CORS issues
                    // CloudFront URL format: https://d23shqpev1klq3.cloudfront.net/{base64EncodedJSON}
                    try {
                        const pathPart = url.split('cloudfront.net/')[1];
                        if (pathPart) {
                            const decoded = JSON.parse(atob(pathPart));
                            const s3Key = decoded.key;
                            
                            // Check if the key is already in the format we need
                            if (s3Key && (s3Key.startsWith('public/') || s3Key.includes('profile-pictures/'))) {
                                // Normalize the key
                                const cleanKey = s3Key.startsWith('public/') ? s3Key : `public/${s3Key}`;
                                
                                // Try to fetch from direct S3 URL first (should not have CORS issues if bucket is public)
                                try {
                                    const directS3Url = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                                    const response = await fetch(directS3Url);
                                    
                                    if (response.ok) {
                                        const blob = await response.blob();
                                        const fileName = 'profile.jpg';
                                        file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                                    } else {
                                        throw new Error('Profilbild konnte nicht geladen werden.');
                                    }
                                } catch (directS3Error) {
                                    // If direct S3 URL fails, try using Amplify's getUrl
                                    try {
                                        // Remove 'public/' prefix for getUrl (it adds it automatically)
                                        const keyForGetUrl = cleanKey.startsWith('public/') ? cleanKey.replace('public/', '') : cleanKey;
                                        const s3UrlResult = await getUrl({
                                            key: keyForGetUrl,
                                            options: {
                                                accessLevel: 'public',
                                                validateObjectExistence: false
                                            }
                                        });
                                        
                                        // Try fetching from the URL returned by getUrl
                                        const response = await fetch(s3UrlResult.url.toString());
                                        if (response.ok) {
                                            const blob = await response.blob();
                                            const fileName = 'profile.jpg';
                                            file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                                        } else {
                                            throw new Error('Profilbild konnte nicht geladen werden.');
                                        }
                                    } catch (s3Error) {
                                        console.error('Error fetching from S3:', s3Error);
                                        // If both methods fail, require new upload
                                        throw new Error('Bitte wähle ein neues Profilbild aus. Das vorhandene Bild kann nicht verwendet werden.');
                                    }
                                }
                            } else {
                                throw new Error('Bitte wähle ein neues Profilbild aus. Das vorhandene Bild kann nicht verwendet werden.');
                            }
                        } else {
                            throw new Error('Bitte wähle ein neues Profilbild aus. Das vorhandene Bild kann nicht verwendet werden.');
                        }
                    } catch (decodeError) {
                        console.error('Error decoding CloudFront URL:', decodeError);
                        // If we can't decode the URL, require a new image
                        throw new Error('Bitte wähle ein neues Profilbild aus. Das vorhandene Bild kann nicht verwendet werden.');
                    }
                }
                // For other URLs (direct S3 URLs, etc.), try to fetch
                else {
                    try {
                        const response = await fetch(url, {
                            mode: 'cors',
                            credentials: 'omit'
                        });
                        if (response.ok) {
                            const blob = await response.blob();
                            const fileName = url.split('/').pop() || 'profile.jpg';
                            file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                        } else {
                            throw new Error('Profilbild konnte nicht geladen werden.');
                        }
                    } catch (fetchError) {
                        throw new Error('Bitte wähle ein neues Profilbild aus. Das vorhandene Bild kann nicht verwendet werden.');
                    }
                }
            }
            
            if (!file) {
                throw new Error('Bitte lade ein Profilbild hoch.');
            }

            const uploadResult = await uploadOwnedMedia({
                file,
                entityType: 'CREATIVE',
                entityId: creativeId,
                mediaKind: 'IMAGE',
                slot: 'profile',
                fileName: imageData?.fileName || file.name || 'profile.jpg',
            });

            return uploadResult.key;
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        }
    };

    const handlePublish = async () => {
        if (isPublishing) return;

        // Validate the last step (AboutService) first
        const isLastStepValid = await aboutServiceValidation.current();
        if (!isLastStepValid) {
            setHasError(true);
            setIsPublishing(false);
            isPublishingRef.current = false;
            return;
        }

        // Validate all previous steps as well
        const isChooseStudioValid = await chooseStudioValidation.current();
        const isChooseCreativeProfileValid = await chooseCreativeProfileValidation.current();
        const isAddUserInfosValid = await addUserInfosValidation.current();

        if (!isChooseStudioValid || !isChooseCreativeProfileValid || !isAddUserInfosValid) {
            setHasError(true);
            setIsPublishing(false);
            isPublishingRef.current = false;
            return;
        }

        let uploadedProfilePictureKey = null;
        let createdCreative = null;
        const creativeId = uuid();

        try {
            setPublishError(null); // Clear any previous errors
            setIsPublishing(true);
            isPublishingRef.current = true;

            // Validate required fields (additional checks)
            if (!serviceData?.selectedStudioId) {
                throw new Error('Bitte wähle ein Studio aus.');
            }
            // Validate creative type is set (should be "VISUAL" or "MUSIC")
            if (!serviceData?.creativeType || (serviceData?.creativeType !== "VISUAL" && serviceData?.creativeType !== "MUSIC")) {
                throw new Error('Bitte wähle einen Kreativbereich aus.');
            }
            
            // Check displayName - from serviceData or fallback to user's name
            const publishDisplayName = serviceData?.displayName?.trim() || 
                (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : '') ||
                user?.displayName?.trim() || '';
            
            if (!publishDisplayName) {
                throw new Error('Bitte gib einen Anzeigenamen ein.');
            }
            if (!serviceData?.jobTitle) {
                throw new Error('Bitte gib eine Jobbezeichnung ein.');
            }
            // Validate profile picture - accept file (newly selected) or url (existing)
            const hasProfilePicture = 
                (serviceData?.profilePicture?.file) || 
                (serviceData?.profilePicture?.url) || 
                (user?.profilePicture);
            
            if (!hasProfilePicture) {
                throw new Error('Bitte lade ein Profilbild hoch.');
            }
            if (!serviceData?.description || serviceData?.description?.trim().length < 10) {
                throw new Error('Die Beschreibung muss mindestens 10 Zeichen lang sein.');
            }
            if (serviceData?.description?.length > 100) {
                throw new Error('Die Beschreibung darf höchstens 100 Zeichen lang sein.');
            }
            if (!serviceData?.skills || serviceData?.skills?.length < 3) {
                throw new Error('Du musst mindestens 3 Skills & Services auswählen.');
            }

            // Upload profile picture
            // If serviceData.profilePicture is not set but user has a profile picture, use that
            const profilePictureToUpload = serviceData?.profilePicture || 
                (user?.profilePicture ? { url: user.profilePicture } : null);
            
            if (!profilePictureToUpload) {
                throw new Error('Bitte lade ein Profilbild hoch.');
            }
            
            uploadedProfilePictureKey = await uploadMedia(profilePictureToUpload, creativeId);

            // Create Creative profile
            // creativeType is now "VISUAL" or "MUSIC"
            const visualTypes = serviceData.creativeType === 'VISUAL'
                ? (
                    serviceData.visualTypes?.length > 0
                        ? serviceData.visualTypes
                        : getVisualTypesFromCreativeCategories(serviceData.creativeCategories)
                )
                : [];

            const creativeData = {
                id: creativeId,
                userID: userID,
                creativeType: serviceData.creativeType, // "VISUAL" or "MUSIC"
                visualTypes,
                profilePicture: uploadedProfilePictureKey,
                displayName: serviceData.displayName,
                jobTitle: serviceData.jobTitle,
                description: serviceData.description,
                skills: serviceData.skills || [],
                status: 'DRAFT'
            };

            createdCreative = await handleCreateCreative(creativeData);

            if (!createdCreative?.id) {
                throw new Error('Creative-Profil konnte nicht erstellt werden.');
            }

            // Link Creative to Studio
            const linkData = {
                studioID: serviceData.selectedStudioId,
                creativeID: createdCreative.id
            };

            const studioCreativeLink = await handleCreateStudioCreative(linkData);

            if (!studioCreativeLink?.id) {
                throw new Error('Creative-Profil konnte nicht mit dem Studio verknüpft werden.');
            }

            trackServiceCreationComplete({
                creativeId: createdCreative.id,
                studioId: serviceData.selectedStudioId,
                creativeType: serviceData.creativeType,
                source: 'service_creation'
            });

            // Clear context
            clearServiceData();

            // Navigate directly into the newly created creative's overview page
            navigate(`/creative-editing/${createdCreative.id}/overview`, {
                replace: true,
            });

        } catch (error) {
            console.error('Service konnte nicht veröffentlicht werden:', error);
            if (uploadedProfilePictureKey && !createdCreative?.id) {
                try {
                    await deleteOwnedMedia({
                        key: uploadedProfilePictureKey,
                        entityType: 'CREATIVE',
                        entityId: creativeId,
                        mediaKind: 'IMAGE',
                    });
                } catch (cleanupError) {
                    console.warn('Profilbild-Cleanup nach fehlgeschlagener Creative-Erstellung ist fehlgeschlagen:', cleanupError);
                }
            }
            notifySaveError(error, {
                title: 'Profil konnte nicht veröffentlicht werden',
                message: getServicePublishErrorMessage(error),
            });
            isPublishingRef.current = false;
            setIsPublishing(false);
            setPublishError('Ups, etwas ist mit dem System schiefgelaufen. Bitte versuche es später erneut.');
            
            // Clear error after 5 seconds
            setTimeout(() => {
                setPublishError(null);
            }, 5000);
        }
    };

    useEffect(() => {
        return () => {
            if (serviceData.profilePicture && serviceData.profilePicture.url) {
                URL.revokeObjectURL(serviceData.profilePicture.url);
            }
        };
    }, [serviceData.profilePicture]);

    return (
        <div
            ref={containerRef}
            className={classes.container}
        >
            <header className={classes.header}>
                <div className={classes.headerContainer}>
                    <Link to="/" className={classes.logoContainer} onClick={(e) => handleExitClick(e, '/')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                            <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                            <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                            <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                        </svg>
                        <span className={`${classes.currentStep} regularMiddle`}>{stepTexts[currentSlide]}</span>
                    </Link>
                    <Link to="/" className={classes.closeContainer} onClick={(e) => handleExitClick(e, '/')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                        </svg>
                    </Link>
                </div>
                <div className={classes.headerContainer2} style={{ justifyContent: 'flex-start', gap: '100px' }}>
                    <Link to="/" className={classes.logoContainer} onClick={(e) => handleExitClick(e, '/')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                            <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                            <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                            <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                        </svg>
                    </Link>
                    <Link to="/" className={classes.closeContainer} onClick={(e) => handleExitClick(e, '/')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                        </svg>
                    </Link>
                    <div className={classes.headerButtonsContainer}>
                        <span className={`${classes.currentStep} regularMiddle`}>{stepTexts[currentSlide]}</span>
                    </div>
                </div>
                <div className={classes.progressBarTop}>
                    <div
                        className={classes.progressBarInner}
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
            </header>
            <div
                className={classes.carousel}
                ref={carouselRef}
            >
                <div className={`${classes.slide}`}>
                    {slides[currentSlide]}
                </div>
                {publishError && (
                    <div style={{ width: '100%', marginTop: '12px' }}>
                        <ErrorMessage style={{ textAlign: 'center', justifyContent: 'center' }}>
                            {publishError}
                        </ErrorMessage>
                    </div>
                )}
            </div>
            <footer className={classes.footer}>
                <div className={classes.progressBar}>
                    <div
                        className={classes.progressBarInner}
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
                <div className={classes.footerContainer}>
                    <div onClick={prevSlide} className={classes.footerButtonsContainer}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                            <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
                            <line x1="0.626536" y1="8.54651" x2="9.62508" y2="18.2949" stroke="#1F1F1F" strokeWidth="1.7"/>
                        </svg>
                        <button className={`${classes.footerBackButton} mediumLarge`}>Zurück</button>
                    </div>
                    {currentSlide !== slides.length - 1 && (
                        <button
                            onClick={nextSlide}
                            className={`${classes.footerWeiterButton} mediumLarge`}
                        >
                            Weiter
                        </button>
                    )}
                    {currentSlide === slides.length - 1 && (
                        <>
                            <button
                                onClick={handlePublish}
                                disabled={isPublishing || createCreativeLoading || linkLoading}
                                className={`${classes.footerWeiterButton} mediumLarge`}
                            >
                                {isPublishing || createCreativeLoading || linkLoading ? 'Erstellen...' : 'Service erstellen'}
                            </button>
                        </>
                    )}
                </div>
            </footer>

            {/* Exit Confirmation Modal */}
            <CancelPopUp
                fromServiceCreation={true}
                showExitModal={showExitModal}
                cancelExit={cancelExit}
                confirmExit={confirmExit}
            />

            {/* Loading Creation Modal */}
            {(isPublishing || createCreativeLoading || linkLoading) && (
                <LoadingCreationModal 
                    fromServiceCreation={true}
                    studioImage={serviceData?.profilePicture?.url} 
                />
            )}
        </div>
    );
};

export default ServiceErstellenPage;
